/**
 * Pure helpers for glyph-deletion preflight and feature-code cleanup.
 * Font.preflightDeleteGlyphs / Font.deleteGlyphs own font access.
 *
 * Feature/prefix cleanup is intentionally NOT a FEA parser: it comments out
 * individual source lines that contain deleted glyph-name tokens. That keeps
 * `feature tag { ... }` wrappers intact (unlike `;`-statement deletion).
 */
const FEA_TOKEN_RE = /#[^\n]*|@[A-Za-z0-9_.-]+|[A-Za-z0-9_.-]+/g;
const DELETED_GLYPH_COMMENT_MARKER = '[deleted glyph]';
function normalizeKernGroupName(groupName) {
    return groupName.startsWith('@') ? groupName.slice(1) : groupName;
}
/**
 * Kerning side keys for delete preflight / cleanup.
 *
 * - related*: glyph names + every `@class` containing a deleted glyph (preview).
 * - removed*: glyph names + `@class` keys for classes that become empty after
 *   removing deleted members (pairs that deleteGlyphs actually drops).
 */
export function buildAffectedKerningKeys(deletedNames, firstGroups, secondGroups) {
    const relatedLeftKeys = new Set(deletedNames);
    const relatedRightKeys = new Set(deletedNames);
    const removedLeftKeys = new Set(deletedNames);
    const removedRightKeys = new Set(deletedNames);
    const addClassKeys = (groups, related, removed) => {
        for (const [groupName, members] of Object.entries(groups || {})) {
            if (!Array.isArray(members)) {
                continue;
            }
            if (!members.some((member) => deletedNames.has(member))) {
                continue;
            }
            const classKey = `@${normalizeKernGroupName(groupName)}`;
            related.add(classKey);
            const remaining = members.filter((member) => !deletedNames.has(member));
            if (remaining.length === 0) {
                removed.add(classKey);
            }
        }
    };
    addClassKeys(firstGroups, relatedLeftKeys, removedLeftKeys);
    addClassKeys(secondGroups, relatedRightKeys, removedRightKeys);
    return {
        relatedLeftKeys,
        relatedRightKeys,
        removedLeftKeys,
        removedRightKeys
    };
}
/**
 * Walk nested or flat kerning maps. Flat entries use "left:right" keys.
 */
export function forEachKerningPair(kerning, onPair) {
    if (!kerning || typeof kerning !== 'object') {
        return;
    }
    for (const [key, value] of Object.entries(kerning)) {
        if (typeof value === 'number') {
            const separator = key.indexOf(':');
            if (separator < 0) {
                continue;
            }
            onPair(key.slice(0, separator), key.slice(separator + 1), value);
            continue;
        }
        if (!value || typeof value !== 'object') {
            continue;
        }
        for (const [right, pairValue] of Object.entries(value)) {
            if (typeof pairValue === 'number') {
                onPair(key, right, pairValue);
            }
        }
    }
}
export function kerningPairIsAffected(left, right, leftKeys, rightKeys) {
    return leftKeys.has(left) || rightKeys.has(right);
}
/** True when the map stores flat "left:right" → number pairs. */
export function isFlatKerningMap(kerning) {
    if (!kerning || typeof kerning !== 'object') {
        return false;
    }
    return Object.entries(kerning).some(([key, value]) => typeof value === 'number' || key.includes(':'));
}
/**
 * Drop pairs whose left/right keys are affected by a glyph deletion.
 * Preserves flat vs nested storage shape.
 */
export function filterKerningMap(kerning, leftKeys, rightKeys) {
    const flat = isFlatKerningMap(kerning);
    if (flat) {
        const next = {};
        forEachKerningPair(kerning, (left, right, value) => {
            if (kerningPairIsAffected(left, right, leftKeys, rightKeys)) {
                return;
            }
            next[`${left}:${right}`] = value;
        });
        return next;
    }
    const next = {};
    forEachKerningPair(kerning, (left, right, value) => {
        if (kerningPairIsAffected(left, right, leftKeys, rightKeys)) {
            return;
        }
        if (!next[left]) {
            next[left] = {};
        }
        next[left][right] = value;
    });
    return next;
}
/**
 * Strip # comments and "..." / '...' strings so brace/`featureNames`
 * detection is not confused by descriptive text.
 */
function stripFeaCommentsAndStrings(line) {
    let result = '';
    let index = 0;
    while (index < line.length) {
        const char = line[index];
        if (char === '#') {
            break;
        }
        if (char === '"' || char === "'") {
            const quote = char;
            index += 1;
            while (index < line.length && line[index] !== quote) {
                if (line[index] === '\\') {
                    index += 2;
                    continue;
                }
                index += 1;
            }
            index += 1;
            result += ' ';
            continue;
        }
        result += char;
        index += 1;
    }
    return result;
}
function createFeatureNamesScanState() {
    return { depth: 0, pending: false };
}
/**
 * Advance featureNames-section tracking for one source line.
 * Returns whether this line is inside (or entering) a featureNames block.
 */
function consumeFeatureNamesLine(line, state) {
    const stripped = stripFeaCommentsAndStrings(line);
    const wasInside = state.depth > 0 || state.pending;
    if (!wasInside && /\bfeatureNames\b/.test(stripped)) {
        state.pending = true;
    }
    const inside = state.depth > 0 || state.pending;
    if (!inside) {
        return false;
    }
    for (const char of stripped) {
        if (char === '{') {
            state.depth += 1;
            state.pending = false;
        }
        else if (char === '}') {
            state.depth = Math.max(0, state.depth - 1);
        }
    }
    return wasInside || inside;
}
function forEachDeletedGlyphToken(code, deletedNames, onMatch) {
    code.replace(FEA_TOKEN_RE, (token) => {
        if (!token.startsWith('#') &&
            !token.startsWith('@') &&
            deletedNames.has(token)) {
            onMatch();
        }
        return token;
    });
}
/**
 * Walk feature/prefix code line-by-line, skipping featureNames { } sections
 * (name/description blocks, not glyph rules).
 */
function forEachFeatureCodeLineOutsideFeatureNames(code, onLine) {
    const state = createFeatureNamesScanState();
    for (const line of code.split('\n')) {
        const insideFeatureNames = consumeFeatureNamesLine(line, state);
        onLine(line, insideFeatureNames);
    }
}
/** Count glyph-name tokens (not comments or @classes) that match deleted names. */
export function countDeletedGlyphTokensInFeatureCode(code, deletedNames) {
    let count = 0;
    forEachFeatureCodeLineOutsideFeatureNames(code, (line, insideFeatureNames) => {
        if (insideFeatureNames) {
            return;
        }
        forEachDeletedGlyphToken(line, deletedNames, () => {
            count += 1;
        });
    });
    return count;
}
/**
 * Collect 1-based source lines (outside featureNames) that reference deleted
 * glyph names. Used for delete-confirm preview panels.
 */
export function collectFeatureLinesReferencingDeletedGlyphs(code, deletedNames) {
    const lines = [];
    let lineNumber = 0;
    forEachFeatureCodeLineOutsideFeatureNames(code, (line, insideFeatureNames) => {
        lineNumber += 1;
        if (insideFeatureNames) {
            return;
        }
        const body = line.trim();
        if (!body || body.startsWith('#')) {
            return;
        }
        if (featureCodeReferencesDeletedGlyph(line, deletedNames)) {
            lines.push({ lineNumber, text: line });
        }
    });
    return lines;
}
export function featureCodeReferencesDeletedGlyph(code, deletedNames) {
    let found = false;
    forEachDeletedGlyphToken(code, deletedNames, () => {
        found = true;
    });
    return found;
}
/**
 * Remove deleted glyph-name tokens from class source (space-separated lists).
 * Preserves comments and @class references; collapses leftover whitespace.
 */
export function stripDeletedGlyphTokensFromClassCode(code, deletedNames) {
    const stripped = code.replace(FEA_TOKEN_RE, (token) => {
        if (!token.startsWith('#') &&
            !token.startsWith('@') &&
            deletedNames.has(token)) {
            return '';
        }
        return token;
    });
    return stripped
        .split('\n')
        .map((line) => line
        .replace(/[ \t]{2,}/g, ' ')
        .replace(/^[ \t]+/, '')
        .replace(/[ \t]+$/g, ''))
        .join('\n');
}
/**
 * @deprecated Use stripDeletedGlyphTokensFromClassCode or
 * commentOutFeatureLinesReferencingDeletedGlyphs.
 */
export function stripDeletedGlyphTokensFromFeatureCode(code, deletedNames) {
    return stripDeletedGlyphTokensFromClassCode(code, deletedNames);
}
/**
 * Comment out feature/prefix source lines that reference deleted glyphs.
 * Line-based (not `;`-statement-based) so `feature tag {` headers stay intact.
 * Skips `featureNames { }` sections (localized names/descriptions).
 */
export function commentOutFeatureLinesReferencingDeletedGlyphs(code, deletedNames) {
    if (!code || deletedNames.size === 0) {
        return code;
    }
    const lines = [];
    forEachFeatureCodeLineOutsideFeatureNames(code, (line, insideFeatureNames) => {
        if (insideFeatureNames) {
            lines.push(line);
            return;
        }
        const leading = line.match(/^\s*/)?.[0] ?? '';
        const body = line.slice(leading.length);
        if (!body || body.startsWith('#')) {
            lines.push(line);
            return;
        }
        if (!featureCodeReferencesDeletedGlyph(body, deletedNames)) {
            lines.push(line);
            return;
        }
        if (body.includes(DELETED_GLYPH_COMMENT_MARKER)) {
            lines.push(line);
            return;
        }
        lines.push(`${leading}# ${DELETED_GLYPH_COMMENT_MARKER} ${body}`);
    });
    return lines.join('\n');
}
